import { useNotifications } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast } from '@/app/providers';
import type { Notification, NotificationPriority } from '@yawi/domain';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Bell, CheckCheck, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRouteFromNotification } from '@/utils/notificationDeepLink';

const getPriorityColor = (priority: NotificationPriority): string => {
  switch (priority) {
    case 'URGENT':
      return 'border-l-red-500 bg-red-50';
    case 'HIGH':
      return 'border-l-amber-500 bg-amber-50';
    case 'NORMAL':
      return 'border-l-blue-500 bg-blue-50';
    case 'LOW':
    default:
      return 'border-l-slate-300 bg-slate-50';
  }
};

const formatTime = (dateString: string, locale: 'es' | 'en'): string => {
  try {
    const dateLocale = locale === 'es' ? es : enUS;
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: dateLocale });
  } catch {
    return dateString;
  }
};

interface NotificationItemProps {
  notification: Notification;
  onNotificationClick: (notification: Notification) => void;
  locale: 'es' | 'en';
}

const NotificationItem = ({ notification, onNotificationClick, locale }: NotificationItemProps) => {
  const isUnread = notification.status !== 'READ';

  return (
    <div
      className={`p-3 border-l-4 transition-colors cursor-pointer hover:bg-slate-100 ${getPriorityColor(notification.priority)} ${isUnread ? '' : 'opacity-70'}`}
      onClick={() => onNotificationClick(notification)}
    >
      <div className="flex items-start gap-2">
        {isUnread && <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isUnread ? 'font-semibold text-slate-900' : 'text-slate-700'} truncate`}>
            {notification.title}
          </p>
          <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{notification.body}</p>
          <p className="text-xs text-slate-400 mt-1">{formatTime(notification.createdAt, locale)}</p>
        </div>
      </div>
    </div>
  );
};

export const NotificationBell = () => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { currentUser } = useServices();
  const toast = useToast();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications({
    enablePolling: true,
    limit: 10,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.status !== 'READ') {
      setNavigatingId(notification.id);
    }

    const route = getRouteFromNotification(notification.data, notification.type);

    if (!route) {
      if (notification.status !== 'READ') {
        await markAsRead(notification.id);
      }
      setIsOpen(false);
      setNavigatingId(null);
      return;
    }

    if (!currentUser) {
      sessionStorage.setItem('pendingNotificationRoute', route);
      setIsOpen(false);
      navigate('/login', { state: { from: route } });
      return;
    }

    try {
      await navigate(route);
      if (notification.status !== 'READ') {
        await markAsRead(notification.id);
      }
      setIsOpen(false);
    } catch {
      toast.error(t('notifications.errorNavigating') || 'Unable to open that notification');
    } finally {
      setNavigatingId(null);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notificaciones');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-theme-secondary hover:text-theme hover:bg-surface-secondary rounded-lg transition-colors"
        aria-label={t('notifications.title')}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-white">
            <h3 className="font-bold text-slate-800">{t('notifications.title')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  title={t('notifications.markAllRead')}
                >
                  <CheckCheck size={16} />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto bg-white">
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-primary-500 rounded-full mx-auto" />
                <p className="text-sm mt-2">{t('common.loading')}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Bell size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('notifications.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onNotificationClick={handleNotificationClick}
                    locale={language as 'es' | 'en'}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-slate-100 bg-slate-50">
              <button
                onClick={handleViewAll}
                className="block w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t('notifications.viewAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
